import { OneConversationSchema } from "@/features/Prompt/schema";
import { toast } from "@/hooks/use-toast";
import {
  useAssistantRuntime,
  useThreadListItemRuntime,
  useThreadRuntime,
} from "@assistant-ui/react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useClient, usePathRoute } from "./ClientContext";
import {
  createNewThread,
  fetchConversationDataWithErrorHandling,
  isConversationAlreadyLoaded,
  processAndImportConversation,
  storeThreadReference,
  switchToExistingThread,
} from "./utils";

/*
 * Extract thread ID from the current URL path
 * @returns The thread ID from the URL and the loading state
 */
export function useThreadIdFromUrl(): string | undefined {
  const location = useLocation();
  return location.pathname.split("/")[3];
}

/**
 * Hook for loading and managing thread data
 * @returns Thread runtime state and functions
 */
export function useThreadLoader() {
  // to store the internal assistant-ui thread id and the backend conversation id
  // to avoid loading the same conversation multiple times
  const loadedConversations = useRef(new Map<string, string>());
  const threadId = useThreadIdFromUrl();
  const client = useClient();
  const path_route = usePathRoute();

  // to get the internal assistant-ui thread id
  const threadListItemRuntime = useThreadListItemRuntime();

  // to create a new thread and / or switch to an existing thread
  const assistantRuntime = useAssistantRuntime();

  // to import messages into the thread runtime
  const threadRuntime = useThreadRuntime();

  // For error handling and navigation
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadThread = async () => {
      if (!threadId) {
        setIsLoading(false);
        return;
      }

      if (isConversationAlreadyLoaded(loadedConversations.current, threadId)) {
        switchToExistingThread(
          assistantRuntime,
          loadedConversations.current,
          threadId,
        );
        setIsLoading(false);
        return;
      }

      // Fetch conversation data with error handling
      const conversationData = await fetchConversationDataWithErrorHandling(
        threadId,
        client,
        path_route,
        navigate,
      );

      // If there was an error, we've already navigated away
      if (!conversationData) {
        setIsLoading(false);
        return;
      }

      await createNewThread(assistantRuntime);

      const importSuccess = processAndImportConversation(
        conversationData,
        OneConversationSchema,
        threadRuntime,
      );

      if (importSuccess) {
        await storeThreadReference(
          threadListItemRuntime,
          loadedConversations.current,
          threadId,
        );
      }
      setIsLoading(false);
    };

    loadThread();
  }, [
    threadId,
    client,
    assistantRuntime,
    threadRuntime,
    threadListItemRuntime,
    navigate,
    toast,
  ]);

  return {
    threadId,
    isLoading,
  };
}
